import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Printer } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const DynamicQRCodeGenerator = () => {
    const [error, setError] = useState(null);
    const [unavailStb, setUnavailStb] = useState([]);
    const [staffInp, setStaffInp] = useState("");
    const [loading, setLoading] = useState(true);
    const [inputs, setInputs] = useState([{ id: 1, value: "" }]);
    const [qrCodeValues, setQRCodeValues] = useState([]);
    const [staff, setStaff] = useState([]);
    const qrCodesRef = useRef(null);
    const navigate = useNavigate();

    function getCookie(name) {
        const cookies = document.cookie.split("; ");
        for (const cookie of cookies) {
            const [key, value] = cookie.split("=");
            if (key === name) return value;
        }
        return null;
    }

    const user = getCookie("user");

    useEffect(() => {
        if (!user) {
            navigate("/login");
        } else {
            fetchData();
        }
    }, [navigate, user]);

    const addInputField = () => {
        setInputs([...inputs, { id: inputs.length + 1, value: "" }]);
    };

    const handleInputChange = (index, event) => {
        const newInputs = [...inputs];
        newInputs[index].value = event.target.value;
        setInputs(newInputs);
    };

    async function fetchData() {
        try {
            const response = await axios.post("https://node.cabletvpay.in/qrCodeNeeds", { user });
            if (response.status === 200) {
                setStaff(response.data.staff);
            }
        } catch (error) {
            setError(error.response?.data?.message || "Error fetching data");
        } finally {
            setLoading(false);
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault();

        try {
            if (staffInp && !inputs[0].value) {
                const response = await axios.post("https://node.cabletvpay.in/qrCodeVerify", { user, staffInp });
                if (response.status === 200) {
                    setQRCodeValues(response.data.result);
                }
            } else if (!staffInp && inputs[0].value) {
                const response = await axios.post("https://node.cabletvpay.in/qrCodeVerify", { user, inputs });
                if (response.status === 200) {
                    setQRCodeValues(response.data.result);
                }
            } else if (!staffInp && !inputs[0].value) {
                toast.warning("Select at least one required field");
                return;
            } else {
                toast.warning("Select staff only or STB only allowed");
                return;
            }
        } catch (error) {
            setError(error.response?.data?.message || "Error fetching data");
        }
    };

    useEffect(() => {
        if (qrCodeValues.length > 0) {
            const unavailable = qrCodeValues.filter((value) => !value.data).map((value) => value.serial_num);
            setUnavailStb(unavailable);
        }
    }, [qrCodeValues]);

    const handlePrint = () => {
        const printWindow = window.open('', '', 'width=800,height=600');
        if (!printWindow) {
            toast.error("Unable to open print window. Please check your popup settings.");
            return;
        }

        const style = `
            <style>
                @media print {
                    @page {
                        size: A4;
                        margin: 10mm;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: 0;
                    }
                    .qr-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 10mm;
                        padding: 5mm;
                    }
                    .qr-item {
                        break-inside: avoid;
                        page-break-inside: avoid;
                        border: 1px solid #ccc;
                        padding: 5mm;
                        border-radius: 2mm;
                        display: flex;
                        align-items: center;
                        gap: 5mm;
                    }
                    .qr-details {
                        flex: 1;
                    }
                    .qr-details p {
                        margin: 2mm 0;
                        font-size: 10pt;
                    }
                    .qr-code {
                        width: 140px;
                        height: 140px;
                    }
                }
            </style>
        `;

        const validQRCodes = qrCodeValues.filter(value => value.data);
        const content = validQRCodes.map(value => {
            const qrCodeDataUrl = document.getElementById(`qr-${value.serial_num}`);
            return `
                <div class="qr-item">
                    <img class="qr-code" src="${qrCodeDataUrl?.toDataURL()}" alt="QR Code" />
                    <div class="qr-details">
                        <p style="font-weight: bold; color: #dc3545;">Code: ${value.code}</p>
                        <p style="color: #0dcaf0;">${value.Name}</p>
                        <p style="color: #198754;">${value.address}</p>
                        <p>STB: ${value.serial_num}</p>
                    </div>
                </div>
            `;
        }).join('');

        const html = `
            <!DOCTYPE html>
            <html>
                <head>
                    <title>QR Codes</title>
                    ${style}
                </head>
                <body>
                    <div class="qr-grid">
                        ${content}
                    </div>
                </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.onafterprint = () => {
                printWindow.close();
            };
        };
    };

    return (
        <div className="content-wrapper bg-white">
            <div className="container-fluid">
                <div className="content-header pt-1">
                    <div className="mt-3 border-bottom">
                        <div className="fs-4 valuesDec h5 my-4 text-bold">QR Code Generator</div>
                        <ToastContainer theme="dark" />
                    </div>
                    <div className="flex flex-col items-center p-6 bg-gray-100 min-h-screen">
                        <div className="card w-full">
                            <div className="card-header">
                                <h3 className="font-bold">Dynamic QR Code Generator</h3>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit} className="bg-white p-6 shadow-md rounded-lg">
                                    <div className="row">
                                        <div className="col-lg-2 p-2 col-md-4 col-sm-6">
                                            <select
                                                name="staff"
                                                id="staff"
                                                className="form-control"
                                                onChange={(e) => setStaffInp(e.target.value)}
                                            >
                                                <option value="">Select a staff</option>
                                                {staff.length > 0 ? (
                                                    staff.map((row, i) => (
                                                        <option key={i} value={`${row.c}`}>
                                                            {`${row.n} - (${row.c})`}
                                                        </option>
                                                    ))
                                                ) : (
                                                    <option value="" disabled>
                                                        No staff available
                                                    </option>
                                                )}
                                            </select>
                                        </div>
                                        {inputs.map((input, index) => (
                                            <div key={input.id} className="col-lg-2 p-2 col-md-4 col-sm-6">
                                                <input
                                                    type="text"
                                                    className="form-control qrInp"
                                                    placeholder={`Enter STB no : ${index + 1}`}
                                                    value={input.value}
                                                    onChange={(e) => handleInputChange(index, e)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="card-footer mt-2 d-flex justify-content-end">
                                        <button type="button" onClick={addInputField} className="bg-green-500 text-white px-4 py-2 rounded-md mt-2 mr-2">
                                            More +
                                        </button>
                                        <button type="submit" className="bg-blue-500 text-white px-4 py-2 rounded-md mt-2">
                                            Generate QR Code
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                        {qrCodeValues.length > 0 && (
                            <div className="w-full bg-white shadow-md rounded-lg p-4 mt-4" ref={qrCodesRef}>
                                <div className="flex justify-between items-center mb-3">
                                    <h3 className="font-bold">Generated QR Codes</h3>
                                    {unavailStb.length > 0 && (
                                        <div className="text-red-500">
                                            STB not found: {unavailStb.join(", ")}
                                        </div>
                                    )}
                                    <button 
                                        onClick={handlePrint} 
                                        className="bg-blue-500 text-white px-4 py-2 rounded-md flex items-center"
                                    >
                                        <Printer className="mr-2" size={18} />
                                        Print QR Codes
                                    </button>
                                </div>
                                <div className="row" id="qr-codes-container">
                                    {qrCodeValues.map(
                                        (value, ind) =>
                                            value.data && (
                                                <div className="col-lg-4 mb-4" key={ind}>
                                                    <div className="border rounded p-2 qr-card">
                                                        <div className="d-flex py-1">
                                                            <QRCodeCanvas 
                                                                id={`qr-${value.serial_num}`}
                                                                value={value.serial_num} 
                                                                size={140} 
                                                                level="H" 
                                                                includeMargin 
                                                                fgColor="#002040" 
                                                                bgColor="#F0F0F0" 
                                                            />
                                                            <div className="ml-3">
                                                                <p className="font-bold text-danger"><i className="fas fa-key" /> Code: {value.code}</p>
                                                                <p className="text-cyan"><i className="fas fa-user" /> {value.Name}</p>
                                                                <p className="text-success"><i className="fas fa-map-marker-alt" /> {value.address}</p>
                                                                <p><i className="far fa-hdd" /> STB: {value.serial_num}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DynamicQRCodeGenerator;